// Updated Login.js with fixed error message for Google login
import React, { useState, useContext } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useMutation } from "@apollo/client";
import axios from "axios";
import {
  Backdrop,
  CircularProgress,
  OutlinedInput,
  Typography,
  Button,
  Link,
  Box,
  Grid,
  Paper,
  SvgIcon
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom"; 
import jwtDecode from "jwt-decode"; 
import { LOGIN_USER, SIGNUP_USER_GOOGLE } from "../../../graphql/mutations";
import useLoadReduxStoreData from "../../../redux/reducers/loadReduxStoreData";
import { BusinessQueryNames } from "../../../graphql/queries"; 
import { useAuthStatus } from "../../../util/useAuthStatus";

const GoogleIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M21.35 11.1h-9.18v2.94h5.27c-.23 1.25-.95 2.31-2.03 3.02v2.5h3.29c1.92-1.76 3.03-4.36 3.03-7.46 0-.5-.04-.98-.11-1.45z" fill="#4285F4" />
    <path d="M12.17 22c2.75 0 5.06-.91 6.75-2.47l-3.29-2.5c-.91.61-2.07.97-3.46.97-2.66 0-4.91-1.8-5.72-4.21H3.74v2.65C5.43 19.8 8.07 22 12.17 22z" fill="#34A853" />
    <path d="M6.45 13.62a6.97 6.97 0 0 1 0-4.38V6.6H3.74a11.77 11.77 0 0 0 0 8.8l2.71-2.65z" fill="#FBBC05" />
    <path d="M12.17 5.36c1.49 0 2.83.51 3.88 1.51l2.91-2.91C17.23 2.23 14.92 1 12.17 1 8.07 1 5.43 3.2 3.74 6.6l2.71 2.65c.81-2.41 3.06-4.21 5.72-4.21z" fill="#EA4335" />
    <path d="M1 1h22v22H1z" fill="none" />
  </SvgIcon>
);

const Login = (props) => {
    const { isLoggedIn } = useAuthStatus();
  const { refetch: reloadBusinesses } = useLoadReduxStoreData(
    BusinessQueryNames.GET_MY_BUSINESSES,
    { enabled: isLoggedIn }
  );

  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState(new URLSearchParams(window.location.search).get("email") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState([]);
  const [updating, setUpdating] = useState(false);

    const [signUpWithGoogleMutation] = useMutation(SIGNUP_USER_GOOGLE);
  const [loginUser] = useMutation(LOGIN_USER);

  const getRedirectRoute = (decodedToken) => {
    const defaultRoute = decodedToken?.defaultBusinessId ? "/biz/calendar" : "/";
    return location.state?.from || defaultRoute;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError([]);

    if (!email.trim()) {
      setError((prev) => [...prev, "Email is required"]);
      return;
    }
    if (!password.trim()) {
      setError((prev) => [...prev, "Password is required"]);
      return;
    }

    try {
      const result = await loginUser({ variables: { email, password } });
      const token = result.data.login.token;
      localStorage.setItem("token", token);
      props.login(token);
      const decoded = token !== "undefined" ? jwtDecode(token) : null;

      if (decoded?.defaultBusinessId && decoded?.staffId) {
        reloadBusinesses().finally(() => navigate(getRedirectRoute(decoded), { replace: true }));
      } else {
        navigate(getRedirectRoute(decoded), { replace: true });
      }
    } catch (err) {
      setError([err.message || "Login failed. Please try again."]);
    }
  };

  const login = useGoogleLogin({
    onSuccess: (response) => {
      axios
        .get(
          `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${response.access_token}`,
          {
            headers: {
              Authorization: `Bearer ${response.access_token}`,
              Accept: "application/json",
            },
          }
        )
        .then((res) => {
          signUpWithGoogleMutation({
            variables: {
              email: res.data.email,
              username: res.data.name,
            },
          })
            .then((result) => {
              const token = result?.data?.signUpGoogle?.token;
              localStorage.setItem("token", token);
              props.login(token);
              const decoded = jwtDecode(token);
              navigate(getRedirectRoute(decoded), { replace: true });
            })
            .catch((err) => {
              if (err.message === "Validation error") {
                setError([`Google login failed: ${err.message}`]);
              }
            });
        });
    },
    onFailure: (error) => console.log("Google sign-up failed:", error),
  });

  return (
    <Box display="flex" justifyContent="center" alignItems="center" marginTop="50px" flexDirection="column">
      <Paper elevation={3} style={{ padding: "30px", maxWidth: "400px", width: "100%" }}>
        <Typography variant="h5" align="center" gutterBottom>
          Login to Your Account
        </Typography>
        <form onSubmit={handleSubmit} style={{ marginBottom: "20px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <OutlinedInput
                fullWidth
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <OutlinedInput
                fullWidth
                placeholder="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Grid>
          </Grid>
          <Box mt={2}>
            <Link href="/forgotpassword" underline="hover">
              Forgot your password?
            </Link>
          </Box>
          <Button variant="contained" color="primary" type="submit" fullWidth style={{ marginTop: "20px" }}>
            Login
          </Button>
        </form>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          onClick={() => login()}
          startIcon={<GoogleIcon />}
          style={{ marginBottom: "20px" }}
        >
          Sign in with Google
        </Button>
        {error.length > 0 && (
          <Box mt={2}>
            {error.map((err, index) => (
              <Typography key={index} color="error" align="left" style={{ marginBottom: "4px" }}>
                {`- ${err}`}
              </Typography>
            ))}
          </Box>
        )}
        <Typography align="center" variant="body2" style={{ marginTop: "10px" }}>
          Don’t have an account? <Link href="/signup">Sign Up</Link>
        </Typography>
        <Backdrop open={updating} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress />
          <Typography variant="h6" style={{ marginLeft: "10px" }}>
            Signing you in...
          </Typography>
        </Backdrop>
      </Paper>
    </Box>
  );
};

export default Login;
